import apiClient from "../api-client.js";

// Provider-neutral on purpose: no path or message here names the provider.

const BASE = "/integrations/rank-tracking";

// Same limits as the backend, which revalidates them.
export const KEYWORDS_PER_ENGINE = 15;
export const MAX_COMPETITORS = 20;

// Provision and update run within the request: a big edit can take a couple of minutes.
const PROVISION_TIMEOUT = 5 * 60 * 1000;

// Classic engines only in v1; the backend filters too, this guards a catalogue that grows first.
export const CLASSIC_ENGINE_FAMILIES = ["google", "bing", "yahoo"];

export const AI_ENGINES = [
  { id: "google_ai_overview", label: "AI Overviews", title: "Google AI Overviews", located: true },
  { id: "google_ai_mode", label: "AI Mode", title: "Google AI Mode", located: true },
  { id: "chatgpt", label: "ChatGPT", title: "ChatGPT", located: false },
  { id: "gemini", label: "Gemini", title: "Gemini", located: true },
  { id: "perplexity", label: "Perplexity", title: "Perplexity", located: false },
];

export const CATALOG_STALE_TIME = 24 * 60 * 60 * 1000;

export const rankTrackingKeys = {
  countries: () => ["rank-tracking", "countries"],
  engines: (country) => ["rank-tracking", "engines", country],
  locations: (country, q) => ["rank-tracking", "locations", country, q],
  languages: () => ["rank-tracking", "languages"],
  config: (clientId) => ["rank-tracking", "config", clientId],
};

const asList = (data) => (Array.isArray(data) ? data : data?.results || []);

/** `[{ code, name, region_id }]` */
export const getRankTrackingCountries = async () => {
  const response = await apiClient.get(`${BASE}/catalog/countries/`);
  return asList(response.data);
};

/** `[{ family, device, search_engine_id, name }]`, classic engines of one country. */
export const getRankTrackingEngines = async (country) => {
  const response = await apiClient.get(`${BASE}/catalog/engines/`, { params: { country } });
  return asList(response.data).filter((engine) => CLASSIC_ENGINE_FAMILIES.includes(engine.family));
};

/** `[{ region_name, label }]`, at most 50, searched server side. */
export const getRankTrackingLocations = async (country, q = "") => {
  const response = await apiClient.get(`${BASE}/catalog/locations/`, {
    params: { country, q: q || undefined },
  });
  return asList(response.data);
};

/** `[{ code, name }]` — Google interface languages. */
export const getRankTrackingLanguages = async () => {
  const response = await apiClient.get(`${BASE}/catalog/languages/`);
  return asList(response.data);
};

/** `{ status, link_id, site_id, steps, counts, failed, message }` once the project is set up. */
export const provisionRankTracking = async (payload) => {
  const response = await apiClient.post(`${BASE}/provision/`, payload, { timeout: PROVISION_TIMEOUT });
  return response.data || {};
};

/** `{ linkId, siteDomain, config }`: the draft last applied to the client's managed project. */
export const getRankTrackingConfig = async (clientId) => {
  const response = await apiClient.get(`${BASE}/config/`, { params: { client: clientId } });
  const data = response.data || {};
  return { linkId: data.link_id, siteDomain: data.site_domain || "", config: data.config || null };
};

/** The draft is the whole desired state: whatever it leaves out is removed, history included. */
export const updateRankTracking = async (payload) => {
  const response = await apiClient.post(`${BASE}/update/`, payload, { timeout: PROVISION_TIMEOUT });
  return response.data || {};
};

const PROVISION_BLOCKS = ["general", "engines", "keywords", "competitors"];
// Server fields shown in another step's block.
const BLOCK_OF_FIELD = { ai_engines: "engines", brand: "engines" };

const CONFLICT_MESSAGES = {
  managed_project_exists: "Este cliente ya tiene un proyecto SEO configurado.",
  linked_project: "Este cliente ya tiene un proyecto SEO vinculado.",
  provision_in_progress: "Ya hay una configuración en curso para este cliente.",
  no_managed_project: "Este cliente no tiene un proyecto SEO creado desde Markezy.",
};

const flattenMessages = (value) => {
  if (value == null) return [];
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(flattenMessages);
  if (typeof value === "object") return Object.values(value).flatMap(flattenMessages);
  return [String(value)];
};

// `keywordsByEngine` only fills when the backend keys errors by `engines[].key`.
export const parseProvisionError = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const detail = typeof data?.detail === "string" ? data.detail : "";

  if (status === 400 && data && typeof data === "object") {
    const blocks = {};
    Object.entries(data).forEach(([field, value]) => {
      const block = BLOCK_OF_FIELD[field] || field;
      const messages = flattenMessages(value);
      if (PROVISION_BLOCKS.includes(block) && messages.length) blocks[block] = [...(blocks[block] || []), ...messages];
    });
    const keywordsByEngine =
      data.keywords && typeof data.keywords === "object" && !Array.isArray(data.keywords)
        ? Object.fromEntries(
          Object.entries(data.keywords).map(([key, value]) => [key, flattenMessages(value)])
        )
        : {};
    const other = Object.entries(data)
      .filter(([key]) => !PROVISION_BLOCKS.includes(BLOCK_OF_FIELD[key] || key))
      .flatMap(([, value]) => flattenMessages(value));
    return {
      status,
      blocks,
      keywordsByEngine,
      message: other[0] || "Revisa los datos marcados antes de crear el proyecto.",
    };
  }

  if (status === 409) {
    return {
      status,
      code: data?.code || null,
      blocks: {},
      keywordsByEngine: {},
      message: detail || CONFLICT_MESSAGES[data?.code] || CONFLICT_MESSAGES.provision_in_progress,
    };
  }

  if (status === 403) {
    return {
      status,
      blocks: {},
      keywordsByEngine: {},
      message: "No tienes permiso para configurar el seguimiento SEO de este cliente.",
    };
  }

  // The server may still be finishing what the browser stopped waiting for.
  if (err?.code === "ECONNABORTED" || status === 504) {
    return {
      status,
      blocks: {},
      keywordsByEngine: {},
      message:
        "La configuración está tardando más de lo habitual y puede que siga en marcha. Revisa la pestaña de Visibilidad en unos minutos.",
    };
  }

  return {
    status,
    blocks: {},
    keywordsByEngine: {},
    message: "No se ha podido completar la configuración. Inténtalo de nuevo en unos minutos.",
  };
};
